import type { CurrentUser } from '@capsio/contracts';
import { useState, type ReactNode } from 'react';
import { api, messageOf } from '../api-client.js';
import { BusyButton, Field, PasswordMeter } from '../components/Field.js';

/**
 * 改密码。两种进入方式共用一套表单：
 *
 * - `forced` —— 拿管理员给的密码进来的人，先改再谈别的。做成不可关闭的遮罩而不是可跳过的提示：
 *   那串密码经过别人的手，只要他还没改，它就同时躺在两个人的聊天记录里。
 * - 自愿 —— 从左栏账号浮层点进来，可以取消。
 *
 * 后端改完会撤掉这个人所有会话再给当前请求发一个新的，所以本机不掉线、其它设备全掉。
 */
export function ChangePasswordDialog({
  forced,
  onDone,
  onClose,
}: {
  forced: boolean;
  onDone: (user: CurrentUser) => void;
  onClose: () => void;
}): ReactNode {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (next.length < 12) {
      setError('新密码至少 12 位');
      return;
    }
    if (confirm !== next) {
      setError('两次输入不一致');
      return;
    }
    if (next === current) {
      setError('新密码和当前密码一样');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const updated = await api.changePassword(current, next);
      // 强制那条改完就该放他进系统，成功本身就是反馈，不用再点一次「知道了」。
      if (forced) onDone(updated);
      else {
        setDone(true);
        onDone(updated);
      }
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="backdrop"
      onClick={(event) => {
        // 强制那条点遮罩不关，本来就不该有退路。
        if (!forced && event.target === event.currentTarget) onClose();
      }}
    >
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="pw-title">
        {done ? (
          <>
            <div className="m-head">
              <h2 id="pw-title">密码已经改了</h2>
              <p>下次登录用新的那串。你在其他设备上的登录已经全部失效，这台还在。</p>
            </div>
            <button className="btn" type="button" onClick={onClose}>
              知道了
            </button>
          </>
        ) : (
          <>
            <div className="m-head">
              <h2 id="pw-title">{forced ? '先改一下密码' : '改密码'}</h2>
              <p>
                {forced
                  ? '你现在用的是管理员给的密码，它经过别人的手，改掉之后才算你自己的账号。'
                  : '改完之后其他设备上的登录会全部失效，这台不受影响。'}
              </p>
            </div>
            <form onSubmit={(event) => void submit(event)}>
              <Field id="cur-pw" label="当前密码" aux={forced ? '管理员给你的那串' : undefined}>
                <input
                  id="cur-pw"
                  type="password"
                  autoComplete="current-password"
                  value={current}
                  onChange={(event) => setCurrent(event.target.value)}
                />
              </Field>
              <Field
                id="next-pw"
                label="新密码"
                aux="至少 12 位"
                hint={<PasswordMeter value={next} />}
              >
                <input
                  id="next-pw"
                  type="password"
                  autoComplete="new-password"
                  value={next}
                  onChange={(event) => setNext(event.target.value)}
                />
              </Field>
              <Field id="next-pw2" label="再输一次" error={error}>
                <input
                  id="next-pw2"
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(event) => setConfirm(event.target.value)}
                />
              </Field>

              {forced ? (
                <BusyButton busy={busy} busyLabel="保存中" type="submit">
                  改好了
                </BusyButton>
              ) : (
                <div className="row-actions">
                  <button className="btn quiet" type="button" onClick={onClose}>
                    取消
                  </button>
                  <BusyButton busy={busy} busyLabel="保存中" type="submit">
                    改密码
                  </BusyButton>
                </div>
              )}
            </form>
          </>
        )}
      </div>
    </div>
  );
}
